import { Filter } from './Filter';
import { getLocationsHierarchy, getZones as getAllZones, getGates as getAllGates, getCrossingLines as getAllCrossingLines, getMedia as getAllMedia, getCampaigns as getAllCampaigns, getPlaylists as getAllPlaylists, getAccount as getAccountData } from '../assets/libs/broox/brooxCloudApi';
import { AccountParams } from './AccountParams';
import { cachedRequest, requestKey } from './RequestCache';

// The location hierarchy is requested by the filter bar and for the categories on every load
const LOCATIONS_TTL = 5 * 60000;

interface Collapsed {
  id: string,
  name: string
}

const getCategories = async (filter: Filter, params: AccountParams): Promise<string[]> => {
  const locations = await getLocations(filter, params);
  const categories: string[] = [];
  for(let location of locations) {
    for(let installation of location.installations) {
      for(let campaign of (installation as any).campaigns) {
        for(let category of campaign.categories) {
          if(!categories.includes(category)) {
            categories.push(category);
          }
        }
      }
    }
  }
  return categories;
}

const getLocations = async (filter: Filter, params: AccountParams): Promise<any[]> => {
  return await cachedRequest(requestKey('locations', filter, params), LOCATIONS_TTL, () => getLocationsHierarchy(filter, params.host, params.token || params.id, params.apiKey));
}

const getZones = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const zones = await getAllZones(filter, params.host, params.token || params.id, params.apiKey);
  return zones.map((zone: string) => { return { id: zone, name: zone }});
}

const getGates = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const gates = await getAllGates(filter, params.host, params.token || params.id, params.apiKey);
  return gates.map((gate: string) => { return { id: gate, name: gate }});
}

const getCrossingLines = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const crossingLines = await getAllCrossingLines(filter, params.host, params.token || params.id, params.apiKey);
  return crossingLines.map((crossingLine: string) => { return { id: crossingLine, name: crossingLine }});
}

const getCampaigns = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const campaigns = await getAllCampaigns(filter, params.host, params.token || params.id, params.apiKey);
  return campaigns.map(c => { return { id: c.id, name: c.name }});
}

const getPlaylists = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const playlists = await getAllPlaylists(filter, params.host, params.token || params.id, params.apiKey);
  return playlists.map(p => { return { id: p.id, name: p.name }});
}

const getMedia = async (filter: Filter, params: AccountParams): Promise<Collapsed[]> => {
  const media = await getAllMedia(filter, params.host, params.token || params.id, params.apiKey);
  return media.map(m => { return { id: m.id, name: m.name }});
}

const getAccount = async (params: AccountParams): Promise<Collapsed> => {
  const account = await getAccountData(params.host, params.token || params.id, params.apiKey);
  return { id: account.id, name: account.name };
}

const findById = (items: Collapsed[], id: string): Collapsed | null => {
  for(let i of items) {
    if(i.id === id) {
      return i;
    }
  }
  return null;
}

export { Collapsed, getLocations, getCategories, getZones, getGates, getCrossingLines, getCampaigns, getPlaylists, getMedia, getAccount, findById };
